import { PlatformLocation, isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Params } from '@angular/router';
import {
  ISbEventPayload,
  ISbStories,
  ISbStoriesParams,
  ISbStory,
  ISbStoryData,
  ISbStoryParams,
  StoryblokBridgeV2,
  storyblokInit,
} from '@storyblok/js';
import {
  BehaviorSubject,
  Observable,
  catchError,
  firstValueFrom,
  forkJoin,
  from,
  lastValueFrom,
  map,
  of,
  shareReplay,
  switchMap,
  take,
} from 'rxjs';
import { ISbComponentType, ISbLinkURLObject } from 'storyblok-js-client';
import { GROWTHBOOK } from '../growthbook';
import { getAllRouteData } from '../lib/get-all-route-data';
import { getFullPath } from '../lib/get-full-path';
import { isIframe } from '../lib/is-iframe';
import { loadScript } from '../lib/load-script';
import { removeTrailingSlash } from '../lib/remove-trailing-slash';
import { PicflowStoryblok } from '../picflow-storyblok';
import { AppConfigService } from '../providers/config.service';
import { customRenderText, fixHtml, removeHtml } from '../storyblok/custom-render-text';
import { customRichTextSchema } from '../storyblok/custom-rich-text';
import { convertStoryblokLinkToUrl } from '../storyblok/pipes/storyblok-link.pipe';
import { getRelationships, getUniqueComponentsList } from '../storyblok/storyblok-find-components';
import { StoryblokLink } from '../storyblok/storyblok-types';
import { BrowserService } from './browser.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { StoryblokHelperService } from './storyblok-helper.service';
import { StoryblokReplaceService } from './storyblok-replace.service';
import { TransferStateService } from './transfer-state.service';

export interface StoryblokSectionStyle {
  _uid: string;
  advanced: string;
  component: string;
  pageWidth: string;
  background: string;
  topSpacing: string;
  bottomSpacing: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type StoryblokComponentInstance = InstanceType<any>;

@Injectable({
  providedIn: 'root',
})
export class StoryblokService implements PicflowStoryblok {
  private platformId = inject<string>(PLATFORM_ID);
  private zone = inject(NgZone);
  private http = inject(HttpClient);
  private browserService = inject(BrowserService);
  private helper = inject(StoryblokHelperService);
  private dataLoader = inject(ObservableDataLoaderService);
  private config = inject(AppConfigService);
  private noSsr = inject<{ noSsr: boolean }>('NO_SSR' as never, { optional: true });
  private route = inject(ActivatedRoute);
  private storyblokReplaceService = inject(StoryblokReplaceService);
  private transferService = inject(TransferStateService);
  private growthBook = inject(GROWTHBOOK);
  private location = inject(PlatformLocation);
  accessToken = 'qit3qOMDswj7KNAg95r4yAtt';
  linkMap = new Map<string, string>();
  components: { [key: string]: unknown } = {};

  public updates = new BehaviorSubject<{ story: ISbStoryData } | undefined>(undefined);
  public updates$ = this.updates.pipe(shareReplay(1));
  storyblokInstance: StoryblokBridgeV2;

  constructor() {
    storyblokInit({
      richText: {
        schema: customRichTextSchema,
      },
    }); // can change in the future to add support for custom resolvers
    if (isPlatformServer(this.platformId)) return;
    if (!isIframe()) return;
    if (this.browserService.isExtension()) return;
    this.initEditor();
  }

  private async initEditor() {
    await loadScript(this.config.storyblokBridgeUrl);
    console.debug('Loading Storyblok JS for preview mode');

    const { StoryblokBridge } = window;

    this.storyblokInstance = new StoryblokBridge({
      preventClicks: true,
    });

    this.storyblokInstance.on(['change', 'published'], () => {
      this.browserService.reload();
    });

    this.storyblokInstance.on(['input'], (e: ISbEventPayload) => {
      this.zone.run(async () => {
        this.resolveRelationships(this.route.snapshot)
          .pipe(take(1))
          .subscribe(relationships => {
            this.helper.tempUpdateCache.set('relationships', relationships);
            if (e.story.content.component === 'root') {
              this.updates.next({ story: this.storyblokReplaceService.replaceStoryValues(e.story) });
            } else {
              this.updates.next({ story: e.story });
            }
            this.loadComponents(e.story);
          });
      });
    });
  }

  private getCacheVersion() {
    return this.transferService.withStateTransferObservable('storyblokCV', () =>
      this.dataLoader.get('storyblokCV', () =>
        this.http
          .get<{
            cv: string;
          }>(`https://api.storyblok.com/v2/cdn/stories?content_type=GET_CV&token=${this.accessToken}`)
          .pipe(map(res => res.cv))
      )
    );
  }

  private getStoryUrl(slug: string, params: ISbStoryParams, cv: string) {
    const query = new URLSearchParams();
    if (params.resolve_links) {
      query.set('resolve_links', params.resolve_links);
    }

    if (params.find_by) {
      query.set('find_by', params.find_by);
    }

    return `https://api.storyblok.com/v2/cdn/stories/${slug}?${query.toString()}&token=${this.accessToken}&version=${
      params.version || 'published'
    }&cv=${cv}`;
  }

  private getStoryObservable(slug: string, params: ISbStoryParams): Observable<ISbStory['data'] | false> {
    const cacheKey = JSON.stringify({ slug, ...params });
    return this.dataLoader.get(cacheKey, () =>
      this.getCacheVersion().pipe(
        switchMap(cv =>
          this.http.get<ISbStory['data']>(this.getStoryUrl(slug, params, cv)).pipe(
            catchError(() => {
              return <Observable<undefined>>of(false);
            }),
            map(res => res || (false as const))
          )
        )
      )
    );
  }

  private getQueryParams(snapshot: ActivatedRouteSnapshot = this.route.snapshot): Params {
    if (Object.keys(snapshot.queryParams).length > 0) {
      return snapshot.queryParams;
    }

    if (this.location?.search) {
      const search = new URLSearchParams(this.location.search);
      const queryParams = Object.fromEntries(search.entries());
      return queryParams;
    }
    return {};
  }

  public getStory(slug: string, route?: ActivatedRouteSnapshot): Observable<ISbStory['data'] | false> {
    if (!slug) return of(false);

    const queryParams = this.getQueryParams(route);
    const version = queryParams['_storyblok'] ? 'draft' : 'published';

    const params: ISbStoryParams = { version };
    return this.getStoryObservable(slug, params).pipe(
      switchMap(res => {
        if (!res) return of(undefined);

        const content = res.story.content;
        if (content.abTest) {
          if (isPlatformServer(this.platformId)) {
            if (this.noSsr) {
              this.noSsr.noSsr = true;
            }
            return of(res);
          }
          this.growthBook.triggerExperiment(content.abTest);
          const featureValue = Object.entries(this.growthBook.getFeatures()).find(([, data]) => {
            return data.rules?.find(rule => rule.name === content.abTest);
          })?.[0];
          const abTestValue = this.growthBook.getFeatureValue(featureValue, '');
          if (abTestValue) return this.getStory(abTestValue, route);
          return of(res);
        }
        return of(res);
      }),
      take(1)
    );
  }

  private buildStoriesUrl(params: ISbStoriesParams, cv: string) {
    const query = new URLSearchParams();
    if (params.content_type) {
      query.set('content_type', params.content_type);
    }
    if (params.starts_with) {
      query.set('starts_with', params.starts_with);
    }
    if (params.with_tag) {
      query.set('with_tag', params.with_tag);
    }
    if (params.page) {
      query.set('page', params.page.toString());
    }
    if (params.per_page) {
      query.set('per_page', params.per_page.toString());
    }
    if (params.excluding_fields) {
      query.set('excluding_fields', params.excluding_fields);
    }
    if (params.by_uuids) {
      query.set('by_uuids', params.by_uuids);
    }
    if (params.sort_by) {
      query.set('sort_by', params.sort_by);
    }

    if (params.filter_query) {
      params.filter_query.forEach(item => {
        query.set(`filter_query[${item.key}][in]`, item.value);
      });
    }

    return `https://api.storyblok.com/v2/cdn/stories?${query.toString()}&token=${this.accessToken}&version=${
      params.version || 'published'
    }&cv=${cv}&resolve_links=url`;
  }

  private getAllStories(
    params: ISbStoriesParams,
    page = 1,
    stories = []
  ): Observable<{
    stories: ISbStoryData[];
    total: number;
    links: (
      | ISbStoryData<
          ISbComponentType<string> & {
            [index: string]: unknown;
          }
        >
      | ISbLinkURLObject
    )[];
  }> {
    const resultsPerPage = 100;

    return this.getCacheVersion().pipe(
      switchMap(cv => {
        const url = this.buildStoriesUrl({ per_page: resultsPerPage, page, ...params }, cv);
        return this.transferService
          .withStateTransferObservable(url, () => {
            return this.http.get<ISbStories['data']>(url, { observe: 'response', transferCache: false }).pipe(
              map(_resp => {
                const total = Number(_resp.headers.get('total')) || 0;
                const resp = _resp.body;
                return {
                  stories: resp.stories,
                  total,
                  links: resp.links,
                };
              })
            );
          })
          .pipe(
            switchMap(resp => {
              resp.links.forEach(link => {
                this.linkMap.set(link.uuid, link['url']);
              });

              stories = [...stories, ...resp.stories];

              if (stories.length < resp.total && !params.per_page) {
                return this.getAllStories(params, page + 1, stories);
              }
              return of({ stories, total: resp.total, links: resp.links });
            })
          );
      })
    );
  }

  public getStories(
    options: ISbStoriesParams,
    route: ActivatedRouteSnapshot = this.route.snapshot
  ): Observable<{ stories: ISbStoryData[]; total: number }> {
    const version = route.queryParams['_storyblok'] ? 'draft' : 'published';
    const cacheKey = JSON.stringify({ ...options, version });

    return this.dataLoader
      .get(cacheKey, () => {
        return this.getAllStories(options).pipe(
          catchError(err => {
            console.error('Error loading stories', err);
            return of({ stories: [], total: 0 });
          })
        );
      })
      .pipe(take(1));
  }

  public async resolveRelationshipsFromStory(story: ISbStoryData, route: ActivatedRouteSnapshot = this.route.snapshot) {
    if (!story) return [];

    let ids = Array.from(new Set(getRelationships(story))).filter(Boolean);
    const allStories = [story];

    while (ids.length) {
      const stories = (
        await lastValueFrom(
          this.getStories(
            { by_uuids: ids.join(','), excluding_fields: 'body,preFooter,topBlur,blurOverlay' }, // excluding page fields because of previews where we anyway only need the meta data
            route
          )
        )
      ).stories;

      ids = [];
      stories.forEach(_story => {
        ids.push(...getRelationships(_story));
      });
      ids = Array.from(new Set(ids)).filter(Boolean);
      allStories.push(...stories);
    }

    return allStories;
  }

  public resolveRelationships(route: ActivatedRouteSnapshot, _path?: string, story?: ISbStory['data']) {
    const path = _path || getAllRouteData(route).storyblokPathOverride || getFullPath(route).join('/') || 'home';
    return this.dataLoader.get(`storyblokRelationships_${path}`, () => {
      return (story ? of(story) : this.getStory(path, route)).pipe(
        switchMap(story => {
          if (!story) return of([]);
          return from(this.resolveRelationshipsFromStory(story.story, route));
        })
      );
    });
  }

  public getUniqueComponentsList(
    route: ActivatedRouteSnapshot,
    _path?: string,
    story?: ISbStory['data']
  ): Observable<Set<string>> {
    const path = removeTrailingSlash(
      _path || getAllRouteData(route).storyblokPathOverride || getFullPath(route).join('/') || 'home'
    );
    return this.dataLoader.get(`uniqueComponentsList_${path}`, () => {
      return forkJoin([
        story ? of(story) : this.getStory(path, route),
        this.resolveRelationships(route, path, story).pipe(take(1)),
      ]).pipe(
        map(([story, relationships]) => {
          if (!story) return new Set<string>([]);
          return getUniqueComponentsList([story.story.content, ...relationships]);
        })
      );
    });
  }

  public getStoryTitle(story: ISbStoryData): string {
    if (story.content.title !== undefined) {
      return story.content.title;
    }
    const block = this.helper.getBlocks(story.content.body, ['Title'], true)[0];
    if (block?.title) {
      return block?.title;
    }
    return story.content.metaTitle ?? 'Picflow';
  }

  public getStoryDescription(story: ISbStoryData): string {
    if (story.content.lead !== undefined) return story.content.lead;
    if (story.content.metaDescription) return story.content.metaDescription;
    const block = this.helper.getBlocks(story.content.body, ['Text'], true)[0];
    if (!block?.content) {
      return 'The next-generation online gallery and proofing platform to boost creative workflows for freelancers and teams.';
    }

    return removeHtml(fixHtml(customRenderText(block.content)));
  }

  public getUrlLink(link: StoryblokLink) {
    return convertStoryblokLinkToUrl(link, this.linkMap);
  }

  public async loadComponents(story: ISbStoryData) {
    if (!story) return;
    const componentTypes = Array.from(
      await firstValueFrom(
        this.getUniqueComponentsList(this.route.snapshot, story.full_slug, <ISbStory['data']>{ story })
      )
    );

    await Promise.all(
      componentTypes.map(async type => {
        if (type === 'ContentGroup') {
          const { ContentGroupComponent } = await import(
            '../storyblok/components/content-group/content-group.component'
          );
          return (this.components[type] = ContentGroupComponent);
        }
        if (type === 'BadgeList') {
          const { BadgeListComponent } = await import('../storyblok/components/badge-list/badge-list.component');
          return (this.components[type] = BadgeListComponent);
        }
        if (type === 'Breadcrumb') {
          const { BreadcrumbComponent } = await import('../storyblok/components/breadcrumb/breadcrumb.component');
          return (this.components[type] = BreadcrumbComponent);
        }
        if (type === 'BlogPostLinkList') {
          const { BlogPostLinkListComponent } = await import(
            '../storyblok/components/blog-post-link-list/blog-post-link-list.component'
          );
          return (this.components[type] = BlogPostLinkListComponent);
        }
        if (type === 'LatestBlogPosts') {
          const { LatestBlogPostsComponent } = await import(
            '../storyblok/components/latest-blog-posts/latest-blog-posts.component'
          );
          return (this.components[type] = LatestBlogPostsComponent);
        }
        if (type === 'Blur') {
          const { StoryblokBlurComponent } = await import(
            '../storyblok/components/storyblok-blur/storyblok-blur.component'
          );
          return (this.components[type] = StoryblokBlurComponent);
        }
        if (type === 'Button') {
          const { StoryblokButtonComponent } = await import(
            '../storyblok/components/storyblok-button/storyblok-button.component'
          );
          return (this.components[type] = StoryblokButtonComponent);
        }
        if (type === 'ButtonList') {
          const { StoryblokButtonListComponent } = await import(
            '../storyblok/components/storyblok-button-list/storyblok-button-list.component'
          );
          return (this.components[type] = StoryblokButtonListComponent);
        }
        if (type === 'CheckList') {
          const { CheckListComponent } = await import('../storyblok/components/check-list/check-list.component');
          return (this.components[type] = CheckListComponent);
        }
        if (type === 'Claim') {
          const { ClaimComponent } = await import('../storyblok/components/claim/claim.component');
          return (this.components[type] = ClaimComponent);
        }
        if (type === 'CompanyLogoList') {
          const { CompanyLogoListComponent } = await import(
            '../storyblok/components/company-logo-list/company-logo-list.component'
          );
          return (this.components[type] = CompanyLogoListComponent);
        }
        if (type === 'Comparison') {
          const { ComparisonComponent } = await import('../storyblok/components/comparison/comparison.component');
          return (this.components[type] = ComparisonComponent);
        }
        if (type === 'Conversion') {
          const { ConvertComponent } = await import('../tools/convert/components/convert/convert.component');
          return (this.components[type] = ConvertComponent);
        }
        if (type === 'ConversionFormatSwitcher') {
          const { ConversionFormatSwitcherComponent } = await import(
            '../tools/convert/components/conversion-format-switcher/conversion-format-switcher.component'
          );
          return (this.components[type] = ConversionFormatSwitcherComponent);
        }
        if (type === 'ConvertFromFormatList') {
          const { ConvertFromFormatListComponent } = await import(
            '../tools/convert/components/convert-from-format-list/convert-from-format-list.component'
          );
          return (this.components[type] = ConvertFromFormatListComponent);
        }
        if (type === 'ConvertToFormatList') {
          const { ConvertToFormatListComponent } = await import(
            '../tools/convert/components/convert-to-format-list/convert-to-format-list.component'
          );
          return (this.components[type] = ConvertToFormatListComponent);
        }
        if (type === 'FaqList') {
          const { FaqListComponent } = await import('../storyblok/components/faq-list/faq-list.component');
          return (this.components[type] = FaqListComponent);
        }
        if (type === 'FeatureItemList') {
          const { FeatureItemListComponent } = await import(
            '../storyblok/components/feature-item-list/feature-item-list.component'
          );
          return (this.components[type] = FeatureItemListComponent);
        }
        if (type === 'Form') {
          const { FormComponent } = await import('../storyblok/components/form/form.component');
          return (this.components[type] = FormComponent);
        }
        if (type === 'GalleryShowcaseList') {
          const { GalleryShowcaseListComponent } = await import(
            '../storyblok/components/gallery-showcase-list/gallery-showcase-list.component'
          );
          return (this.components[type] = GalleryShowcaseListComponent);
        }
        if (type === 'HeroVideo' || type === 'HomeTeaser') {
          const { HeroVideoComponent } = await import('../storyblok/components/hero-video/hero-video.component');
          return (this.components[type] = HeroVideoComponent);
        }
        if (type === 'HeroVideoTabs') {
          const { HeroVideoTabsComponent } = await import(
            '../storyblok/components/hero-video-tabs/hero-video-tabs.component'
          );
          return (this.components[type] = HeroVideoTabsComponent);
        }
        if (type === 'ShowcaseTeaser') {
          const { ShowcaseTeaserComponent } = await import(
            '../storyblok/components/showcase-teaser/showcase-teaser.component'
          );
          return (this.components[type] = ShowcaseTeaserComponent);
        }
        if (type === 'IconWithTextList') {
          const { IconWithTextListComponent } = await import(
            '../storyblok/components/icon-with-text-list/icon-with-text-list.component'
          );
          return (this.components[type] = IconWithTextListComponent);
        }
        if (type === 'Image') {
          const { ImageComponent } = await import('../storyblok/components/image/image.component');
          return (this.components[type] = ImageComponent);
        }
        if (type === 'LargeLinkList') {
          const { LargeLinkListComponent } = await import(
            '../storyblok/components/large-link-list/large-link-list.component'
          );
          return (this.components[type] = LargeLinkListComponent);
        }
        if (type === 'PointList') {
          const { PointListComponent } = await import('../storyblok/components/point-list/point-list.component');
          return (this.components[type] = PointListComponent);
        }
        if (type === 'PinnedBlogPostLink') {
          const { PinnedBlogPostLinkComponent } = await import(
            '../storyblok/components/pinned-blog-post-link/pinned-blog-post-link.component'
          );
          return (this.components[type] = PinnedBlogPostLinkComponent);
        }
        if (type === 'PricingBoxList') {
          const { PricingBoxListComponent } = await import(
            '../storyblok/components/pricing-box-list/pricing-box-list.component'
          );
          return (this.components[type] = PricingBoxListComponent);
        }
        if (type === 'ProductComparisonList') {
          const { ProductComparisonListComponent } = await import(
            '../storyblok/components/product-comparison-list/product-comparison-list.component'
          );
          return (this.components[type] = ProductComparisonListComponent);
        }
        if (type === 'ProductFeatureList') {
          const { ProductFeatureListComponent } = await import(
            '../storyblok/components/product-feature-list/product-feature-list.component'
          );
          return (this.components[type] = ProductFeatureListComponent);
        }
        if (type === 'SubMenu') {
          const { SubMenuComponent } = await import('../storyblok/components/sub-menu/sub-menu.component');
          return (this.components[type] = SubMenuComponent);
        }
        if (type === 'TableOfContents') {
          const { TableOfContentsComponent } = await import(
            '../storyblok/components/table-of-contents/table-of-contents.component'
          );
          return (this.components[type] = TableOfContentsComponent);
        }
        if (type === 'Teaser') {
          const { TeaserComponent } = await import('../storyblok/components/teaser/teaser.component');
          return (this.components[type] = TeaserComponent);
        }
        if (type === 'Text') {
          const { TextComponent } = await import('../storyblok/components/text/text.component');
          return (this.components[type] = TextComponent);
        }
        if (type === 'Title') {
          const { TitleComponent } = await import('../storyblok/components/title/title.component');
          return (this.components[type] = TitleComponent);
        }
        if (type === 'Video') {
          const { VideoComponent } = await import('../storyblok/components/video/video.component');
          return (this.components[type] = VideoComponent);
        }
        if (type === 'MiniComparison') {
          const { MiniComparisonComponent } = await import(
            '../storyblok/components/mini-comparison/mini-comparison.component'
          );
          return (this.components[type] = MiniComparisonComponent);
        }
        if (type === 'CustomRatingDisplay') {
          const { CustomRatingDisplayComponent } = await import(
            '../storyblok/components/custom-rating-display/custom-rating-display.component'
          );
          return (this.components[type] = CustomRatingDisplayComponent);
        }
        if (type === 'RatingDisplay') {
          const { RatingDisplayWrapperComponent } = await import(
            '../storyblok/components/rating-display-wrapper/rating-display-wrapper.component'
          );
          return (this.components[type] = RatingDisplayWrapperComponent);
        }
        if (type === 'BlogMeta') {
          const { BlogMetaComponent } = await import('../storyblok/components/blog-meta/blog-meta.component');
          return (this.components[type] = BlogMetaComponent);
        }
        if (type === 'CompetitorList') {
          const { CompetitorListComponent } = await import(
            '../compare/components/competitor-list/competitor-list.component'
          );
          return (this.components[type] = CompetitorListComponent);
        }
        if (type === 'CompetitorReviewList') {
          const { CompetitorReviewListComponent } = await import(
            '../compare/components/competitor-review-list/competitor-review-list.component'
          );
          return (this.components[type] = CompetitorReviewListComponent);
        }
        if (type === 'CompetitorAlternativesList') {
          const { CompetitorAlternativesListComponent } = await import(
            '../compare/components/competitor-alternatives-list/competitor-alternatives-list.component'
          );
          return (this.components[type] = CompetitorAlternativesListComponent);
        }
        if (type === 'CompetitorPricingPoints') {
          const { CompetitorPricingPointsComponent } = await import(
            '../compare/components/competitor-pricing-points/competitor-pricing-points.component'
          );
          return (this.components[type] = CompetitorPricingPointsComponent);
        }
        if (type === 'CompetitorComparisonTable') {
          const { CompetitorComparisonTableComponent } = await import(
            '../compare/components/competitor-comparison-table/competitor-comparison-table.component'
          );
          return (this.components[type] = CompetitorComparisonTableComponent);
        }
        if (type === 'Carousel') {
          const { CarouselComponent } = await import('../storyblok/components/carousel/carousel.component');
          return (this.components[type] = CarouselComponent);
        }
        if (type === 'TwoColumn') {
          const { TwoColumnComponent } = await import('../storyblok/components/two-column/two-column.component');
          return (this.components[type] = TwoColumnComponent);
        }
        if (type === 'ReviewList') {
          const { ReviewListComponent } = await import('../storyblok/components/review-list/review-list.component');
          return (this.components[type] = ReviewListComponent);
        }
        if (type === 'ReviewCarousel') {
          const { ReviewCarouselComponent } = await import(
            '../storyblok/components/review-carousel/review-carousel.component'
          );
          return (this.components[type] = ReviewCarouselComponent);
        }
        if (type === 'Table') {
          const { TableComponent } = await import('../storyblok/components/table/table.component');
          return (this.components[type] = TableComponent);
        }
        if (type === 'CustomHtml') {
          const { CustomHtmlComponent } = await import('../storyblok/components/custom-html/custom-html.component');
          return (this.components[type] = CustomHtmlComponent);
        }
        if (type === 'Coupon') {
          const { CouponComponent } = await import('../storyblok/components/coupon/coupon.component');
          return (this.components[type] = CouponComponent);
        }
        if (type === 'Toast') {
          const { StoryblokToastComponent } = await import(
            '../storyblok/components/storyblok-toast/storyblok-toast.component'
          );
          return (this.components[type] = StoryblokToastComponent);
        }
        if (type === 'Updates') {
          const { UpdatesComponent } = await import('../storyblok/components/updates/updates.component');
          return (this.components[type] = UpdatesComponent);
        }
        // console.log(type);
      })
    );
  }
}
